import { useMemo } from 'react';
import {
  evaluateSurvey,
  type Item,
  type Block,
  type Section,
  type SurveyDefinition,
} from '@webforge-survey/shared';

import { useRespondentStore } from './answers-store';
import {
  NoteItem,
  SelectOneItem,
  SelectMultipleItem,
  IntegerItem,
  TextItem,
  MatrixItem,
} from './items';
import { HelplinePage } from './HelplinePage';

interface RespondentRendererProps {
  definition: SurveyDefinition;
  /** Index du bloc actuellement affiché (un bloc = un écran). */
  blockIndex: number;
  /** Force d'afficher uniquement la partie consentement si présent. */
  showErrors?: boolean;
}

/**
 * Renderer principal. Pour chaque écran (= bloc) :
 * 1. évalue la définition avec les réponses courantes.
 * 2. filtre les items du bloc à ceux visibles.
 * 3. rend chaque item via le composant correspondant à son type.
 *
 * Le composant est PUREMENT déclaratif : aucune logique de saut n'est codée
 * ici, c'est le moteur partagé qui décide. Si demain le moteur change, la UI
 * suit sans modifications.
 *
 * Les "blocs sensibles" se voient adjoindre un encart `HelplinePage` à la
 * fin (UI_DESIGN §5.5).
 */
export function RespondentRenderer({
  definition,
  blockIndex,
  showErrors,
}: RespondentRendererProps) {
  const answers = useRespondentStore((s) => s.answers);
  const setAnswer = useRespondentStore((s) => s.setAnswer);

  const state = useMemo(
    () => evaluateSurvey(definition, answers),
    [definition, answers],
  );

  const block = definition.blocks[blockIndex];
  if (!block) {
    return (
      <div className="card max-w-prose mx-auto">
        <p className="text-text-muted">Aucun contenu à afficher.</p>
      </div>
    );
  }

  const visible = new Set(state.visibleItems);
  const errorsByName = new Map<string, string>();
  if (showErrors) {
    for (const e of state.errors) {
      // Microcopie non culpabilisante
      errorsByName.set(e.itemName, humanizeError(e.message, e.code));
    }
    for (const n of state.missingRequired) {
      errorsByName.set(n, 'Cette question attend une réponse.');
    }
  }

  // Rassemble tous les items du bloc visibles (avec leur section éventuelle)
  const visibleEntries = collectBlockItems(block).filter((entry) =>
    visible.has(entry.item.name),
  );

  const blockSensitive = isSensitiveBlock(block);

  return (
    <section
      aria-labelledby={`block-${block.id}-title`}
      className="space-y-8"
    >
      {(block.title || block.description) && (
        <header className="space-y-2 max-w-prose">
          {block.title && (
            <h2 id={`block-${block.id}-title`} className="text-2xl">
              {block.title}
            </h2>
          )}
          {block.description && (
            <p className="text-text-muted whitespace-pre-line">
              {block.description}
            </p>
          )}
        </header>
      )}

      {visibleEntries.length === 0 && (
        <p className="text-text-muted italic max-w-prose">
          Aucune question ne s'applique dans cette section — vous pouvez
          continuer.
        </p>
      )}

      {/* Items groupés par section (si les blocs en utilisent) */}
      {renderGrouped(visibleEntries, errorsByName, answers, setAnswer)}

      {block.trailingNote && (
        <div className="info-callout max-w-prose whitespace-pre-line">
          {block.trailingNote}
        </div>
      )}

      {blockSensitive && (
        <HelplinePage
          variant="inline"
          title="Ressources d'aide — disponibles à tout moment"
        />
      )}
    </section>
  );
}

interface ItemEntry {
  item: Item;
  section?: Section;
}

function collectBlockItems(block: Block): ItemEntry[] {
  const out: ItemEntry[] = [];
  if (block.items) for (const item of block.items) out.push({ item });
  if (block.sections) {
    for (const section of block.sections) {
      for (const item of section.items) out.push({ item, section });
    }
  }
  return out;
}

function renderGrouped(
  entries: ItemEntry[],
  errorsByName: Map<string, string>,
  answers: ReturnType<typeof useRespondentStore.getState>['answers'],
  setAnswer: ReturnType<typeof useRespondentStore.getState>['setAnswer'],
) {
  /*
   * On rend une suite. Si plusieurs items partagent la même section,
   * on ouvre un sub-heading <h3> avec le titre de la section.
   */
  const elements: React.ReactNode[] = [];
  let lastSectionId: string | undefined;

  for (const entry of entries) {
    if (entry.section && entry.section.id !== lastSectionId) {
      lastSectionId = entry.section.id;
      if (entry.section.title) {
        elements.push(
          <h3
            key={`sec-${entry.section.id}`}
            className="text-lg text-text-muted font-medium border-l-2 border-accent pl-3 max-w-prose"
          >
            {entry.section.title}
          </h3>,
        );
      }
    } else if (!entry.section) {
      lastSectionId = undefined;
    }
    elements.push(
      renderItem(entry.item, errorsByName.get(entry.item.name), answers, setAnswer),
    );
  }
  return elements;
}

function renderItem(
  item: Item,
  error: string | undefined,
  answers: ReturnType<typeof useRespondentStore.getState>['answers'],
  setAnswer: ReturnType<typeof useRespondentStore.getState>['setAnswer'],
): React.ReactNode {
  // Items cachés (échelle scientifique cachée — Bloc 2 V16) ne sont jamais
  // affichés. Sécurité défense en profondeur (le moteur les visibilise quand
  // même mais le rendu ignore).
  if (item.meta?.hidden_scale) return null;

  const key = `item-${item.name}`;
  switch (item.type) {
    case 'note':
      return (
        <div key={key}>
          <NoteItem item={item} />
        </div>
      );
    case 'select_one': {
      const value = (answers[item.name] as string | undefined) ?? null;
      return (
        <div key={key}>
          <SelectOneItem
            item={item}
            value={value}
            {...(error !== undefined && { error })}
            onChange={(v) => setAnswer(item.name, v)}
          />
        </div>
      );
    }
    case 'select_multiple': {
      const raw = answers[item.name];
      const value = Array.isArray(raw) ? (raw as string[]) : [];
      return (
        <div key={key}>
          <SelectMultipleItem
            item={item}
            value={value}
            {...(error !== undefined && { error })}
            onChange={(v) => setAnswer(item.name, v)}
          />
        </div>
      );
    }
    case 'integer': {
      const v = answers[item.name];
      const num = typeof v === 'number' ? v : null;
      return (
        <div key={key}>
          <IntegerItem
            item={item}
            value={num}
            {...(error !== undefined && { error })}
            onChange={(n) => setAnswer(item.name, n)}
          />
        </div>
      );
    }
    case 'text_short':
    case 'text_long': {
      const v = answers[item.name];
      const str = typeof v === 'string' ? v : '';
      return (
        <div key={key}>
          <TextItem
            item={item}
            value={str}
            {...(error !== undefined && { error })}
            onChange={(s) => setAnswer(item.name, s)}
          />
        </div>
      );
    }
    case 'matrix': {
      const v = answers[item.name];
      const value: Record<string, string> =
        v && typeof v === 'object' && !Array.isArray(v)
          ? (v as Record<string, string>)
          : {};
      return (
        <div key={key}>
          <MatrixItem
            item={item}
            value={value}
            {...(error !== undefined && { error })}
            onChange={(m) => setAnswer(item.name, m)}
          />
        </div>
      );
    }
    default: {
      const exhaustive: never = item;
      void exhaustive;
      return null;
    }
  }
}

function humanizeError(_message: string, code: string): string {
  switch (code) {
    case 'required':
      return 'Cette question attend une réponse.';
    case 'constraint':
      return "La valeur saisie n'est pas dans la plage attendue.";
    case 'exclusive':
      return 'Cette option ne se combine pas avec les autres.';
    case 'range':
      return "La valeur n'est pas dans l'intervalle autorisé.";
    case 'type':
      return 'Le format de la réponse est inattendu.';
    case 'identity':
      return "Une information d'identification manque ou est incorrecte.";
    case 'expression':
      return 'Le formulaire a rencontré un état inattendu.';
    default:
      return 'Cette réponse demande à être vérifiée.';
  }
}

/**
 * Heuristique simple : un bloc est "sensible" si son id ou son titre évoque
 * les violences, les tensions, l'enfance en danger, le suicide. Dans la
 * définition V16, les blocs concernés sont b11, b12 (couple/violences) — la
 * page de fin déclenche aussi un helpline. On reste conservateur : par
 * défaut, AUCUN bloc n'est marqué sensible ; les blocs métier de la V16 le
 * sont. Pour les autres enquêtes, le créateur peut ajouter un `trailingNote`
 * explicite.
 */
function isSensitiveBlock(block: Block): boolean {
  const SENSITIVE_BLOCK_IDS = new Set(['b11', 'b12']);
  if (SENSITIVE_BLOCK_IDS.has(block.id)) return true;
  const t = (block.title ?? '').toLowerCase();
  return (
    t.includes('violen') ||
    t.includes('tension') ||
    t.includes('suicid') ||
    t.includes('détresse')
  );
}
